/** A chamber tag applied to a document, as shown in a document's tag chips. */
export interface JudgeChambersDocumentTag {
  tagId: string;
  /** Tag display name. */
  name: string;
}

/** A chamber's tag, with how many documents it is applied to. */
export interface JudgeChamberTagOption extends JudgeChambersDocumentTag {
  /** Documents the tag is applied to; a tag in use cannot be deleted. */
  documentCount: number;
}

/** An active chamber member a document can be assigned to. */
export interface JudgeChamberAssignee {
  userUniqueId: string;
  /** Display name (e.g. "Reyes, Marta"). */
  name: string;
}

/** The Assigned To filter value for documents with no assignee. */
export const NOT_ASSIGNED_FILTER = 'not-assigned';

/**
 * The Assigned To filter: null for all documents, `NOT_ASSIGNED_FILTER`, or a
 * member's user id.
 */
export type JudgeChamberAssigneeFilter = string | null;

/** A chamber the user works in. */
export interface JudgeChamberOption {
  chamberId: string;
  /** The chamber judge's display name. */
  judgeName: string;
  courtName: string;
  /** The chamber's tags, sorted by name. */
  tags: JudgeChamberTagOption[];
  /** Active members (the judge first), who documents can be assigned to. */
  members: JudgeChamberAssignee[];
}

/**
 * UI model for the chamber data layered over the judge review queue on the
 * Judge Chambers page. Decoupled from the `JudgeChambersSummary` DTO; mapped
 * once in `map-judge-chambers.utils.ts`.
 */
export interface JudgeChambersView {
  /** Unread notifications for the user across all of their chambers. */
  unreadNotificationCount: number;
  /** Chambers the user is the judge of or an active member of. */
  chambers: JudgeChamberOption[];
  /** Chamber tags on pending documents, keyed by document id and sorted by name. */
  documentTags: ReadonlyMap<number, JudgeChambersDocumentTag[]>;
  /**
   * The chamber whose tags apply to each pending document, keyed by document id
   * (the chamber of the judge the document is pending for).
   */
  documentChambers: ReadonlyMap<number, string>;
  /**
   * The chamber whose notes apply to each case of a pending filing, keyed by
   * case id.
   */
  caseChambers: ReadonlyMap<number, string>;
  /** Note counts for pending documents that have notes, keyed by document id. */
  documentNoteCounts: ReadonlyMap<number, number>;
  /** Note counts for pending cases that have notes, keyed by case id. */
  caseNoteCounts: ReadonlyMap<number, number>;
  /** The assignee's user id for each assigned pending document, keyed by document id. */
  documentAssignees: ReadonlyMap<number, string>;
  /** Each pending case's unique id (opens Case Information), keyed by case id. */
  caseUniqueIds: ReadonlyMap<number, string>;
}

/** The view when the user has no chambers or chamber data failed to load. */
export const EMPTY_JUDGE_CHAMBERS_VIEW: JudgeChambersView = {
  unreadNotificationCount: 0,
  chambers: [],
  documentTags: new Map(),
  documentChambers: new Map(),
  caseChambers: new Map(),
  documentNoteCounts: new Map(),
  caseNoteCounts: new Map(),
  documentAssignees: new Map(),
  caseUniqueIds: new Map(),
};
